<script setup>
/**
 * RED-356 — LIVE copy (Toomas 02.10 15:40): while `qv-statistical-report` is OFF
 * the Kiirhindamine result page must show exactly what LIVE shows today, so
 * this is origin/main 969c0754's ModelQualitySection.vue unchanged (its strings
 * are inline; its two literal no-break spaces are written as backslash-u00a0
 * escapes for ESLint, same output), fed by the server's LIVE-shaped dossier
 * (no `evidence`).
 * ModelQualitySection.vue is the flag-ON version. Delete this file with the flag.
 *
 * One deliberate change from LIVE (Jaan Olev, 13.10): the "CI katte määr (95%)"
 * row is gone. It was the training-time share of test sales inside
 * mean ± 1.96·σ_bag — 24 % on the Harju default — so the "95 %" was false.
 *
 * Mudeli kvaliteedi mõõdikud — Model Quality Metrics tab content.
 *
 * Powered by GET /v2/models/{id}/quality-metrics from the REdata API. Three
 * subscription-tier-aware sections per Jaan Olev's RED-196 framing:
 *
 *   Tier 1 (everyone)     — confidence gauge + grade + verdict
 *   Tier 2 (paid)         — IAAO breakdown + uncertainty stats (accordion)
 *   Tier 3 (internal)     — Spetsialisti vaade: methodology, top features
 *
 * Visibility is driven by Inertia-shared `auth.can` flags
 * (`view-advanced-model-metrics`, `view-internal-model-metrics`).
 *
 * Fail-soft: when `modelQualityMetrics` is null (older valuation row, no
 * model_id, REdata down) we render a friendly "data unavailable" notice
 * instead of breaking the page.
 */
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'

const props = defineProps({
  /**
   * Full payload from /v2/models/{id}/quality-metrics. Required and
   * assumed non-null — the parent (QuickValuationResult.vue) gates the
   * mount on `modelQualityMetrics !== null`, so this component never has
   * to render an "unavailable" state.
   */
  modelQualityMetrics: {
    type: Object,
    required: true,
  },
})

const can = computed(() => usePage().props?.auth?.can ?? {})
const canAdvanced = computed(() => !!can.value['view-advanced-model-metrics'])
const canInternal = computed(() => !!can.value['view-internal-model-metrics'])

const tier1 = computed(() => props.modelQualityMetrics.tier1_summary ?? {})
const tier2 = computed(() => props.modelQualityMetrics.tier2_detail ?? null)
const tier3 = computed(() => props.modelQualityMetrics.tier3_overview ?? null)
const errorModel = computed(() => props.modelQualityMetrics.error_model ?? null)

// 1–10 visual gauge: render 10 ticks, color the first N filled with a
// red→yellow→green gradient. Matches Toomas's "kellade-tulede-viledega"
// 1/3 A4 framing — a glance-friendly indicator.
const confidenceLevel = computed(() => tier1.value?.confidence_level_1_to_10 ?? null)
const ticks = computed(() => {
  const level = confidenceLevel.value ?? 0
  return Array.from({ length: 10 }, (_, i) => {
    const position = i + 1
    return {
      position,
      filled: position <= level,
      // Color shifts from red (low) -> amber (mid) -> green (high)
      color: position <= 3 ? '#EF4444' : position <= 6 ? '#F59E0B' : '#10B981',
    }
  })
})

const gradeColor = computed(() => tier1.value?.grade_color_hex ?? '#94A3B8')

function formatPct(v, decimals = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${Number(v).toFixed(decimals).replace('.', ',')}\u00a0%`
}
function formatEur(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${Math.round(Number(v)).toLocaleString('et-EE')}\u00a0€`
}
function formatFloat(v, decimals = 3) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return Number(v).toFixed(decimals).replace('.', ',')
}
</script>

<template>
  <div id="model-quality" class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6 mt-6" data-testid="qv-result-model-quality">
    <!-- RED-356 (Toomas 16.10): no technical model id in the header, for anyone.
         Admins still see the run id under "Spetsialisti vaade" (Tier 3). -->
    <div data-testid="qv-model-quality-header" class="flex items-center gap-2 mb-4">
      <span class="pi pi-shield text-slate-500"></span>
      <h3 class="font-medium text-slate-800">Mudeli kvaliteet</h3>
    </div>

    <!-- ───────── Tier 1: confidence gauge + grade + verdict (everyone) ───────── -->
    <div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-2">
        <!-- Gauge -->
        <div class="md:col-span-2">
          <div class="text-xs uppercase tracking-wide text-slate-400 mb-2">
            Usaldusväärsus
          </div>
          <div class="flex items-center gap-1 mb-2">
            <div
              v-for="t in ticks"
              :key="t.position"
              class="h-3 flex-1 rounded-xs transition-colors"
              :style="{
                backgroundColor: t.filled ? t.color : '#E2E8F0',
              }"
              :title="`${t.position}/10`"
            />
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-semibold text-slate-800">
              {{ confidenceLevel ?? '—' }}
            </span>
            <span class="text-sm text-slate-500">/ 10</span>
          </div>
        </div>

        <!-- Grade card -->
        <div class="rounded-xl border border-slate-100 px-4 py-3 text-center">
          <div class="text-xs uppercase tracking-wide text-slate-400 mb-1">Hinne</div>
          <div
            class="text-4xl font-semibold"
            :style="{ color: gradeColor }"
          >
            {{ tier1.grade ?? '—' }}
          </div>
        </div>
      </div>

      <p v-if="tier1.verdict" class="text-sm text-slate-600 mt-2 italic">
        {{ tier1.verdict }}
      </p>

      <!-- Headline metrics row -->
      <div
        v-if="tier1.headline_metrics?.length"
        class="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5"
      >
        <div
          v-for="metric in tier1.headline_metrics"
          :key="metric.label"
          class="rounded-lg border border-slate-100 p-3"
        >
          <div class="text-xs text-slate-500 mb-1">{{ metric.label }}</div>
          <div class="flex items-center gap-1.5">
            <span class="text-lg font-medium text-slate-800">
              {{ metric.value_str }}
            </span>
            <span
              :class="[
                'pi',
                metric.is_good ? 'pi-check-circle text-emerald-500' : 'pi-exclamation-circle text-amber-500',
              ]"
              :title="metric.is_good ? 'Sihtväärtus täidetud' : `Sihtväärtus: ${metric.target_str}`"
            />
          </div>
          <div class="text-xs text-slate-400 mt-0.5">
            Sihtväärtus: {{ metric.target_str }}
          </div>
        </div>
      </div>

      <!-- ───────── Tier 2: advanced view (paid users) ───────── -->
      <Accordion v-if="canAdvanced && tier2" :value="null" class="mt-6">
        <AccordionPanel value="advanced">
          <AccordionHeader>
            <span class="font-medium text-slate-700">Detailne ülevaade</span>
          </AccordionHeader>
          <AccordionContent>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2 text-sm">
              <div>
                <div class="text-xs text-slate-400">R²</div>
                <div class="font-medium text-slate-800">{{ formatFloat(tier2.test_r2) }}</div>
              </div>
              <div>
                <div class="text-xs text-slate-400">MAE</div>
                <div class="font-medium text-slate-800">{{ formatEur(tier2.test_mae_eur) }}</div>
              </div>
              <div>
                <div class="text-xs text-slate-400">RMSE</div>
                <div class="font-medium text-slate-800">{{ formatEur(tier2.test_rmse_eur) }}</div>
              </div>
              <div>
                <div class="text-xs text-slate-400">IAAO Median Ratio</div>
                <div class="font-medium text-slate-800">
                  {{ formatFloat(tier2.iaao?.median_ratio) }}
                </div>
              </div>
              <div>
                <div class="text-xs text-slate-400">IAAO COD</div>
                <div class="font-medium text-slate-800">{{ formatPct(tier2.iaao?.cod) }}</div>
              </div>
              <div>
                <div class="text-xs text-slate-400">IAAO PRD</div>
                <div class="font-medium text-slate-800">
                  {{ formatFloat(tier2.iaao?.prd) }}
                </div>
              </div>
              <div>
                <div class="text-xs text-slate-400">Within ±20%</div>
                <div class="font-medium text-slate-800">
                  {{ formatPct(tier2.iaao?.within_20_pct) }}
                </div>
              </div>
              <div>
                <div class="text-xs text-slate-400">CI laiuse keskmine</div>
                <div class="font-medium text-slate-800">
                  {{ formatPct(tier2.uncertainty?.avg_ci_width_pct) }}
                </div>
              </div>
            </div>

            <!-- Trust score components decomposition -->
            <div
              v-if="tier2.trust_score_components?.length"
              class="mt-5"
            >
              <div class="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Hinde komponendid
              </div>
              <div class="space-y-2">
                <div
                  v-for="c in tier2.trust_score_components"
                  :key="c.name"
                  class="flex items-center justify-between text-sm border-b border-slate-50 pb-1.5"
                >
                  <span class="text-slate-700">{{ c.name }}</span>
                  <span class="text-slate-500">
                    {{ c.value_str }}
                    <span class="text-xs text-slate-400 ml-1">({{ c.target_str }})</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Error model summary -->
            <div v-if="errorModel?.available" class="mt-5 pt-4 border-t border-slate-100">
              <div class="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Vea prognoosi mudel
              </div>
              <div class="text-sm text-slate-600">
                Keskmine viga: {{ formatPct(errorModel.test_mae_pct_points) }}
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <!-- ───────── Tier 3: Spetsialisti vaade (internal/admin only) ───────── -->
      <Accordion v-if="canInternal && tier3 && Object.keys(tier3).length > 0" :value="null" class="mt-3">
        <AccordionPanel value="specialist">
          <AccordionHeader>
            <span class="font-medium text-slate-700">Spetsialisti vaade</span>
            <span class="text-xs text-amber-600 ml-2">(sisemine)</span>
          </AccordionHeader>
          <AccordionContent>
            <div v-if="tier3.identity" class="mb-3 text-sm">
              <div class="text-xs text-slate-400">Mudel</div>
              <div class="font-mono text-xs text-slate-700">
                {{ tier3.identity?.run_id ?? '—' }}
              </div>
            </div>

            <div v-if="tier3.methodology" class="mb-3 text-sm">
              <div class="text-xs text-slate-400 mb-1">Metoodika</div>
              <div class="text-slate-600">
                Target: <code class="text-xs">{{ tier3.methodology?.target_transform ?? '—' }}</code>
                <span v-if="tier3.identity?.n_features" class="ml-3">
                  Tunnuseid: {{ tier3.identity.n_features }}
                </span>
              </div>
            </div>

            <div v-if="tier3.top_30_features?.length" class="mb-3">
              <div class="text-xs text-slate-400 mb-1">
                Tähtsaimad tunnused (top {{ tier3.top_30_features.length }})
              </div>
              <ul class="text-xs text-slate-600 space-y-0.5 max-h-48 overflow-y-auto">
                <li
                  v-for="feature in tier3.top_30_features"
                  :key="feature.feature"
                  class="flex justify-between font-mono"
                >
                  <span>{{ feature.feature }}</span>
                  <span class="text-slate-400 ml-3">
                    {{ formatFloat(feature.importance) }}
                  </span>
                </li>
              </ul>
            </div>

            <div v-if="tier3.known_limitations" class="text-xs text-slate-500 italic mt-3">
              {{ tier3.known_limitations }}
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
  </div>
</template>
